/**
 * Sanity Image Optimization Utilities
 * 
 * Leverages Sanity's built-in Image API to:
 * - Resize images to appropriate dimensions
 * - Compress images for faster loading
 * - Generate responsive srcsets
 * - Auto-convert to modern formats (WebP)
 * 
 * Sanity Image API docs: https://www.sanity.io/docs/image-urls
 */

/**
 * Builds an optimized Sanity image URL with query parameters
 * 
 * @param {string} url - Original Sanity CDN URL
 * @param {Object} options - Optimization options
 * @param {number} options.width - Target width in pixels
 * @param {number} options.height - Target height in pixels (optional)
 * @param {number} options.quality - JPEG/WebP quality (1-100, default: 80)
 * @param {string} options.fit - Fit mode: 'clip', 'crop', 'fill', 'fillmax', 'max', 'scale', 'min' (default: 'crop')
 * @param {string} options.format - Force format: 'jpg', 'png', 'webp' (default: auto)
 * @param {boolean} options.auto - Enable auto format selection (default: true)
 * @returns {string} - Optimized image URL
 */
export function getSanityImageUrl(url, options = {}) {
  // Return original URL if it's not a Sanity image
  if (!url || !url.includes('cdn.sanity.io')) {
    return url;
  }

  const {
    width,
    height,
    quality = 80,
    fit = 'crop',
    format,
    auto = true,
  } = options;

  const params = new URLSearchParams();

  // Add dimensions
  if (width) params.append('w', width);
  if (height) params.append('h', height);

  // Add quality
  params.append('q', quality);

  // Add fit mode
  params.append('fit', fit);

  // Add format
  if (format) {
    params.append('fm', format);
  } else if (auto) {
    params.append('auto', 'format');
  }

  // Remove any existing query params from the URL
  const baseUrl = url.split('?')[0];

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generates a srcset string for responsive images
 * 
 * @param {string} url - Original Sanity CDN URL
 * @param {Array<number>} widths - Array of widths for srcset (default: [400, 800, 1200, 1600, 2000])
 * @param {Object} options - Additional options (quality, fit, etc.)
 * @returns {string} - srcset string
 */
export function getSanityImageSrcSet(url, widths = [400, 800, 1200, 1600, 2000], options = {}) {
  // Return empty string if it's not a Sanity image
  if (!url || !url.includes('cdn.sanity.io')) {
    return '';
  }

  return widths
    .map(width => {
      const optimizedUrl = getSanityImageUrl(url, { ...options, width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
}

/**
 * Preset configurations for common image types
 */
export const imagePresets = {
  // Hero images - large, high quality
  hero: {
    url: (url) => getSanityImageUrl(url, { width: 2000, quality: 85, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [800, 1200, 1600, 2000], { quality: 85, fit: 'crop' }),
    sizes: '100vw',
  },
  
  // Location cards - medium size
  locationCard: {
    url: (url) => getSanityImageUrl(url, { width: 800, height: 600, quality: 80, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [400, 600, 800], { height: 600, quality: 80, fit: 'crop' }),
    sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw',
  },
  
  // Location detail page - large
  locationDetail: {
    url: (url) => getSanityImageUrl(url, { width: 1200, quality: 85, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [600, 900, 1200, 1600], { quality: 85, fit: 'crop' }),
    sizes: '(max-width: 768px) 100vw, 1200px',
  },
  
  // Menu item cards - medium size, square-ish
  menuCard: {
    url: (url) => getSanityImageUrl(url, { width: 600, height: 450, quality: 80, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [300, 450, 600], { height: 450, quality: 80, fit: 'crop' }),
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  },
  
  // Menu item detail - large, square
  menuDetail: {
    url: (url) => getSanityImageUrl(url, { width: 1000, height: 1000, quality: 85, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [400, 600, 800, 1000], { height: 1000, quality: 85, fit: 'crop' }),
    sizes: '(max-width: 1024px) 100vw, 50vw',
  },
  
  // Map popup - small thumbnail
  mapPopup: {
    url: (url) => getSanityImageUrl(url, { width: 400, height: 250, quality: 75, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [320, 400], { height: 250, quality: 75, fit: 'crop' }),
    sizes: '320px',
  },
  
  // About section images - medium
  aboutImage: {
    url: (url) => getSanityImageUrl(url, { width: 800, quality: 80, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [400, 600, 800], { quality: 80, fit: 'crop' }),
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
  },
  
  // Thumbnail - small
  thumbnail: {
    url: (url) => getSanityImageUrl(url, { width: 200, height: 200, quality: 75, fit: 'crop' }),
    srcset: (url) => getSanityImageSrcSet(url, [100, 150, 200], { height: 200, quality: 75, fit: 'crop' }),
    sizes: '200px',
  },
};

/**
 * React component helper - generates props for img tag
 * 
 * @param {string} url - Original Sanity CDN URL
 * @param {string} preset - Preset name from imagePresets
 * @param {Object} customOptions - Custom options to override preset
 * @returns {Object} - Props object with src, srcSet, and sizes
 */
export function getSanityImageProps(url, preset = 'menuCard', customOptions = {}) {
  if (!url) {
    return { src: '', srcSet: '', sizes: '' };
  }

  // If not a Sanity image, return as-is
  if (!url.includes('cdn.sanity.io')) {
    return { src: url, srcSet: '', sizes: '' };
  }

  const presetConfig = imagePresets[preset];
  
  if (!presetConfig) {
    console.warn(`Unknown image preset: ${preset}`);
    return { src: url, srcSet: '', sizes: '' };
  }

  return {
    src: presetConfig.url(url),
    srcSet: presetConfig.srcset(url),
    sizes: customOptions.sizes || presetConfig.sizes,
  };
}

